import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import NoResults from '@app/shared/components/no-results/NoResults';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { Navigate, generatePath } from 'react-router';
import { useLocale } from '@app/core/hooks/useLocale';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import { setValuationInputsSelectedTab } from '@app/core/store/ui-values-slice';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import DLOMTable from './minority-discounts-table/DLOMTable';
import { Card } from '@app/shared/components/card/Card';
import { FormProvider, useForm } from 'react-hook-form';
import { MinorityDiscountsDto } from '@app/shared/models/contracts/project-dto';
import * as projectActions from '@app/core/store/project-slice';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import styles from './minority-discounts.module.scss';

const PARENT_CLASSNAME = 'minority-discounts';

const DLOM: FC = (): JSX.Element => {
  useSetSelectedTab(RouteConstants.DLOM, setValuationInputsSelectedTab);

  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const filteredInstruments = Object.values(capitalStructure.instrumentDefinitions).filter(
    (instrument) => instrument.shouldBeValued
  );

  const { l } = useLocale();

  // Form for volatility fields
  const formMethods = useForm<MinorityDiscountsDto>({
    defaultValues: {
      marketVolatility: project.valuationInputs.minorityDiscounts.marketVolatility,
      peerSetVolatility: project.valuationInputs.minorityDiscounts.peerSetVolatility,
    },
  });

  const { trigger, getValues } = formMethods;
  useTriggerEffect(trigger);

  const submitVolatilityData = () => {
    const formValues = getValues();

    const updatedProjectDraft = {
      ...project,
      valuationInputs: {
        ...project.valuationInputs,
        minorityDiscounts: {
          ...project.valuationInputs.minorityDiscounts,
          marketVolatility: formValues.marketVolatility,
          peerSetVolatility: formValues.peerSetVolatility,
        },
      },
    };

    dispatch(projectActions.updateProjectDraft({ project: updatedProjectDraft }));
  };

  if (project.details.calcMethod === CalcMethod.OPM) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  return (
    <>
      <Card>
        {filteredInstruments.length ? (
          <div className="main-container-padding-remove">
            {filteredInstruments.length > 0 && (
              <Card>
                <FormProvider {...formMethods}>
                  <form
                    onBlur={formMethods.handleSubmit(submitVolatilityData, submitVolatilityData)}>
                    <div>
                      <div className={styles[`${PARENT_CLASSNAME}__volatility-text`]}>
                        {l('_Volatility')}
                      </div>
                      <div className={styles[`${PARENT_CLASSNAME}__row`]}>
                        <FormField
                          name="marketVolatility"
                          label={l('_MarketVolatility')}
                          isGapless
                          {...percentageFieldFormattingProps}
                          data-testid="marketVolatility"
                        />
                        <FormField
                          name="peerSetVolatility"
                          label={l('_PeerSetVolatility')}
                          isGapless
                          {...percentageFieldFormattingProps}
                          data-testid="peerSetVolatility"
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </Card>
            )}
            <DLOMTable />
          </div>
        ) : (
          <NoResults
            message={l('_AddInstrumentsAndTheyllAutomaticallyShowHereForYouToAddDLOMInformation', {
              interpolation: { escapeValue: false },
            })}
          />
        )}
      </Card>
    </>
  );
};

export default DLOM;
